import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./assest_design/Marketing.css";
import marketingManagerImg from "./assets/marketing_manager.png";

export default function Marketing() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title =
      "Marketing, PR, SEO & Brand Growth Services in South Africa | ProCode ZA";

    const setMeta = (name, content, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;

      let tag = document.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        if (property) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMeta(
      "description",
      "ProCode ZA offers marketing, public relations, SEO optimisation, social media management, brand growth support, campaign coordination, launch communication, presentations and digital visibility services in Johannesburg, Cape Town, Durban and across South Africa."
    );
    setMeta(
      "keywords",
      "marketing agency South Africa, SEO services South Africa, public relations South Africa, social media management South Africa, Johannesburg marketing agency, Cape Town SEO services, Durban PR agency, brand strategy South Africa, digital marketing South Africa, ProCode ZA marketing"
    );
    setMeta(
      "og:title",
      "Marketing, PR, SEO & Brand Growth Services in South Africa | ProCode ZA",
      true
    );
    setMeta(
      "og:description",
      "From social media and PR to SEO optimisation, campaign coordination and brand communication, ProCode ZA helps businesses in Johannesburg, Cape Town, Durban and across South Africa grow with stronger visibility and more credible market presence.",
      true
    );
    setMeta("og:type", "website", true);
  }, []);

  return (
    <div className="marketing-page">
      <section className="marketing-hero fade-up">
        <div className="marketing-badge">Full-service growth support</div>

        <h1>
          Strategic Marketing, Communications, PR, SEO & Brand Growth - Beyond the Code
        </h1>

        <p className="marketing-subtitle">
          ProCode ZA does more than build systems and websites. We also support
          how businesses in Johannesburg, Cape Town, Durban and across South
          Africa are positioned, communicated and discovered. From social media
          management and PR to SEO optimisation, campaign coordination, launch
          material, business presentations and digital visibility support, we
          help clients build stronger market presence around what they offer.
        </p>

        <div className="marketing-hero-pills">
          <span>Brand strategy</span>
          <span>Social media & PR</span>
          <span>SEO optimisation</span>
          <span>Campaign coordination</span>
          <span>Launch communication</span>
        </div>

        <div className="marketing-actions">
          <button
            className="marketing-btn primary"
            onClick={() => navigate("/contact")}
            type="button"
          >
            Speak to the Team
          </button>

          <button
            className="marketing-btn secondary"
            onClick={() => navigate("/proquote")}
            type="button"
          >
            Get a ProQuote
          </button>
        </div>
      </section>

      <section className="marketing-overview fade-up delay-1">
        <div className="marketing-overview-card">
          <div className="section-kicker">Why this matters</div>
          <h2>Why businesses need more than just a website or product</h2>
          <p>
            A strong service, platform or product is not enough on its own.
            Businesses also need clear communication, discoverability, brand
            consistency and market-facing material that builds trust. ProCode ZA
            supports that wider growth journey by helping clients improve how
            they present themselves across websites, campaigns, public-facing
            communication and digital channels in South Africa’s competitive
            market.
          </p>
        </div>
      </section>

      <section className="marketing-services fade-up delay-2">
        <div className="section-heading">
          <div className="section-kicker">What we offer</div>
          <h2>Marketing, PR, SEO and communication support services</h2>
          <p>
            Our marketing services are designed to complement websites,
            software, digital products, launches and commercial growth. We help
            businesses across Johannesburg, Cape Town, Durban and the wider
            South African market communicate their value more clearly, improve
            digital visibility and strengthen how they appear in the market.
          </p>
        </div>

        <div className="marketing-grid">
          <div className="marketing-card hover-rise">
            <h3>Brand positioning & communication strategy</h3>
            <p>
              We help businesses define how they communicate value, position
              themselves more clearly and present a more consistent brand to the
              market.
            </p>
          </div>

          <div className="marketing-card hover-rise">
            <h3>Social media management</h3>
            <p>
              Structured support for social platforms that helps brands stay
              active, visible, relevant and professionally represented.
            </p>
          </div>

          <div className="marketing-card hover-rise">
            <h3>Public relations support</h3>
            <p>
              Professional communication support that helps businesses manage
              visibility, credibility and external-facing messaging more
              effectively.
            </p>
          </div>

          <div className="marketing-card hover-rise">
            <h3>SEO optimisation support</h3>
            <p>
              We support search visibility through content structure, messaging,
              discoverability strategy and collaboration with technical teams on
              website optimisation.
            </p>
          </div>

          <div className="marketing-card hover-rise">
            <h3>Campaign & launch coordination</h3>
            <p>
              We help coordinate campaigns, launches and growth initiatives so
              businesses can communicate more effectively when introducing new
              services, websites or products.
            </p>
          </div>

          <div className="marketing-card hover-rise">
            <h3>Presentations & business-facing material</h3>
            <p>
              From decks and proposals to profile material and branded assets,
              we help businesses look more polished and investor-, client- and
              partner-ready.
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-how-we-work fade-up delay-3">
        <div className="section-heading">
          <div className="section-kicker">How we work</div>
          <h2>Our approach to marketing, SEO and growth coordination</h2>
        </div>

        <div className="work-steps">
          <div className="work-step hover-rise">
            <span>01</span>
            <h3>Understand the business and audience</h3>
            <p>
              We begin by understanding the business properly — its services,
              audience, positioning, communication gaps and what it needs to be
              seen for.
            </p>
          </div>

          <div className="work-step hover-rise">
            <span>02</span>
            <h3>Align brand, message and delivery</h3>
            <p>
              We shape communication and content around what the business
              actually offers so the message stays clear, commercially relevant
              and consistent across channels.
            </p>
          </div>

          <div className="work-step hover-rise">
            <span>03</span>
            <h3>Coordinate across teams where needed</h3>
            <p>
              Our marketing support is collaborative. Precious works with
              programme teams, communication stakeholders and technical teams to
              help coordinate campaigns, web messaging, SEO optimisation and
              digital presence across the right channels.
            </p>
          </div>

          <div className="work-step hover-rise">
            <span>04</span>
            <h3>Support visibility and growth practically</h3>
            <p>
              We focus on execution that makes businesses look stronger,
              communicate better and gain more trust, visibility and traction in
              ways that support real growth.
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-profile fade-up delay-4">
        <div className="marketing-profile-card">
          <div className="marketing-profile-image-wrap">
            <img
              src={marketingManagerImg}
              alt="Precious Mahlangu, Marketing Manager at ProCode ZA"
              className="marketing-profile-image float-soft"
            />
          </div>

          <div className="marketing-profile-text">
            <div className="section-kicker">Meet the lead</div>
            <h2>Marketing leadership that helps clients grow with visibility and trust</h2>

            <h3>Precious Mahlangu</h3>
            <span className="marketing-role">
              Marketing Manager · Brand, Communication & Growth
            </span>

            <p>
              Precious Mahlangu leads the marketing, communication and growth
              support side of delivery at ProCode ZA. She helps ensure that
              businesses are not only building strong solutions, but also
              presenting them in ways that are clear, credible and commercially
              effective.
            </p>

            <p>
              With strengths across social media, PR, campaign coordination,
              business-facing communication, presentations and SEO collaboration,
              she supports the market-facing side of growth in a way that feels
              intentional and professional.
            </p>

            <p>
              She also works closely with programme teams and technical teams
              where needed, especially when campaigns, website messaging, SEO
              optimisation, launch communication and brand presentation all need
              to align. That coordination helps businesses strengthen both their
              internal execution and their outward-facing impact.
            </p>

            <blockquote>
              “Strong marketing is not just about visibility. It is about
              helping the right people understand your value, trust your brand
              and act with confidence.”
            </blockquote>
          </div>
        </div>
      </section>

      <section className="marketing-benefits fade-up delay-5">
        <div className="section-heading">
          <div className="section-kicker">Client benefit</div>
          <h2>What clients gain from this support</h2>
        </div>

        <div className="benefits-grid">
          <div className="benefit-box hover-rise">
            <h3>Stronger credibility</h3>
            <p>
              Better communication and brand presentation help businesses appear
              more established, polished and trustworthy.
            </p>
          </div>

          <div className="benefit-box hover-rise">
            <h3>Clearer market messaging</h3>
            <p>
              Businesses communicate what they do more effectively and reduce
              confusion in the client journey.
            </p>
          </div>

          <div className="benefit-box hover-rise">
            <h3>Improved online discoverability</h3>
            <p>
              SEO-aware support helps improve search visibility, site clarity
              and long-term digital reach.
            </p>
          </div>

          <div className="benefit-box hover-rise">
            <h3>Better campaign and launch coordination</h3>
            <p>
              Clients gain stronger support around campaigns, launches, web
              messaging, social media and growth communication.
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-cta fade-up delay-6">
        <h2>Need a team that can support both build and growth?</h2>
        <p>
          Whether you need software, a website, SEO optimisation, social media
          support, PR-aligned communication, campaign coordination, launch
          material or a stronger digital brand presence, ProCode ZA can help
          shape a more complete business offering for teams in Johannesburg,
          Cape Town, Durban and across South Africa.
        </p>

        <div className="marketing-actions center">
          <button
            className="marketing-btn primary"
            onClick={() => navigate("/contact")}
            type="button"
          >
            Contact Us
          </button>

          <button
            className="marketing-btn secondary"
            onClick={() => navigate("/innovation")}
            type="button"
          >
            Explore More Services
          </button>
        </div>
      </section>
    </div>
  );
}